// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0032-app-tags.md
import * as React from 'react';
import { CircleDot, EllipsisVertical, ListFilterPlus, Tag, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterOptionList, type FilterOption } from '@/components/apps-list/FilterOptionList';
import { type TagMode } from '@/components/apps-list/filtering';
import { cn } from '@/lib/utils';
import { type FleetBucket } from '@greenlight/shared';

export interface AppsFilterState {
  bucket: FleetBucket | '';
  tags: string[];
  tagMode: TagMode;
}

interface AppsFilterBarProps {
  state: AppsFilterState;
  onStateChange: (next: AppsFilterState) => void;
  /** Tags in play across the loaded fleet, offered as the tag field's options. */
  availableTags: string[];
  /** Org-wide app count per status bucket, shown beside each Status option. */
  bucketCounts?: Partial<Record<FleetBucket, number>>;
  isIT: boolean;
  className?: string;
}

type FieldId = 'status' | 'tags';

interface FieldDef {
  id: FieldId;
  label: string;
  icon: React.ReactNode;
  operators: FilterOption[];
  options: FilterOption[];
  multiple?: boolean;
  searchPlaceholder?: string;
}

/** One chip. `operator` and `value` stay empty until the user picks them. */
interface Row {
  field: FieldId;
  operator: string | null;
  value: string[];
}

type OpenPart = { field: FieldId; part: 'operator' | 'value' } | null;

const IS: FilterOption[] = [{ value: 'is', label: 'is' }];
const TAG_OPERATORS: FilterOption[] = [
  { value: 'is_any_of', label: 'is any of' },
  { value: 'is_all_of', label: 'is all of' },
];

const BUCKET_OPTIONS: Array<{ value: FleetBucket; label: string; itOnly?: boolean }> = [
  { value: 'deploying', label: 'Deploying' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'suspended', label: 'Paused' },
  { value: 'deleted', label: 'Deleted', itOnly: true },
];

const PART_CLASS =
  "flex items-center gap-2 rounded-md border border-input bg-background px-4 text-sm font-medium shadow-xs outline-none focus-visible:z-10 focus-visible:ring-1 focus-visible:ring-ring dark:bg-input/30 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4";
const POPOVER_CLASS = 'w-auto p-0';

function signature(state: AppsFilterState): string {
  return [
    state.bucket,
    state.tags.map((t) => t.toLowerCase()).join(','),
    state.tags.length ? state.tagMode : '',
  ].join('|');
}

function rowsFromState(state: AppsFilterState): Row[] {
  const rows: Row[] = [];
  if (state.bucket) rows.push({ field: 'status', operator: 'is', value: [state.bucket] });
  if (state.tags.length) {
    rows.push({
      field: 'tags',
      operator: state.tagMode === 'all' ? 'is_all_of' : 'is_any_of',
      value: state.tags,
    });
  }
  return rows;
}

function stateFromRows(rows: Row[]): AppsFilterState {
  const next: AppsFilterState = { bucket: '', tags: [], tagMode: 'any' };
  for (const row of rows) {
    if (!row.operator || row.value.length === 0) continue;
    if (row.field === 'status') next.bucket = row.value[0] as FleetBucket;
    if (row.field === 'tags') {
      next.tags = row.value;
      next.tagMode = row.operator === 'is_all_of' ? 'all' : 'any';
    }
  }
  return next;
}

function valueLabel(field: FieldDef, value: string[]): string {
  if (value.length === 0) return 'Select...';
  if (value.length > 1) return `${value.length} selected`;
  return field.options.find((o) => o.value === value[0])?.label ?? value[0]!;
}

export function AppsFilterBar({
  state,
  onStateChange,
  availableTags,
  bucketCounts,
  isIT,
  className,
}: AppsFilterBarProps): React.JSX.Element {
  // The bar keeps its own rows so a chip still waiting for a value survives a
  // render; the URL is resynced only when it names a different filter set.
  const [rows, setRows] = React.useState(() => rowsFromState(state));
  const stateSignature = signature(state);
  const rowsSignatureRef = React.useRef(stateSignature);
  React.useEffect(() => {
    if (rowsSignatureRef.current === stateSignature) return;
    rowsSignatureRef.current = stateSignature;
    setRows(rowsFromState(state));
  }, [stateSignature]);

  const [addOpen, setAddOpen] = React.useState(false);
  const [openPart, setOpenPart] = React.useState<OpenPart>(null);
  const handoffRef = React.useRef(false);

  const fields: FieldDef[] = [
    {
      id: 'status',
      label: 'Status',
      icon: <CircleDot />,
      operators: IS,
      options: BUCKET_OPTIONS.filter((o) => isIT || !o.itOnly).map(({ value, label }) => ({
        value,
        label,
        count: bucketCounts?.[value],
      })),
    },
    ...(availableTags.length > 0
      ? [
          {
            id: 'tags',
            label: 'Tags',
            icon: <Tag />,
            operators: TAG_OPERATORS,
            options: availableTags.map((t) => ({ value: t, label: t })),
            multiple: true,
            searchPlaceholder: 'Search tags',
          } satisfies FieldDef,
        ]
      : []),
  ];
  const fieldById = (id: FieldId) => fields.find((f) => f.id === id);

  const commit = (next: Row[]) => {
    setRows(next);
    const nextState = stateFromRows(next);
    const nextSignature = signature(nextState);
    if (nextSignature === rowsSignatureRef.current) return;
    rowsSignatureRef.current = nextSignature;
    onStateChange(nextState);
  };
  const updateRow = (field: FieldId, patch: Partial<Row>) =>
    commit(rows.map((r) => (r.field === field ? { ...r, ...patch } : r)));

  // The server narrows on one bucket and one tag set, so each field gets one
  // chip; a field already in the bar is edited there, not added again.
  const addField = (id: FieldId) => {
    const field = fieldById(id)!;
    const single = field.operators.length === 1;
    commit([
      ...rows,
      { field: id, operator: single ? field.operators[0]!.value : null, value: [] },
    ]);
    handoffRef.current = true;
    setAddOpen(false);
    setOpenPart({ field: id, part: single ? 'value' : 'operator' });
  };

  // Focus moves on to the next popover, not back to the trigger that closed.
  const skipFocusOnHandoff = (event: Event) => {
    if (!handoffRef.current) return;
    handoffRef.current = false;
    event.preventDefault();
  };

  const isOpen = (field: FieldId, part: 'operator' | 'value') =>
    openPart?.field === field && openPart.part === part;
  const setPartOpen = (field: FieldId, part: 'operator' | 'value') => (open: boolean) =>
    setOpenPart(open ? { field, part } : null);

  const inUse = new Set(rows.map((r) => r.field));
  const visibleRows = rows.filter((r) => fieldById(r.field));
  const compact = visibleRows.length > 0;

  return (
    <div
      data-slot="filters"
      data-active={compact || undefined}
      className={cn('flex flex-wrap items-center', className)}
    >
      <div
        role="toolbar"
        aria-label="Filters"
        aria-orientation="horizontal"
        className="flex flex-wrap items-center gap-1.5"
      >
        {visibleRows.map((row) => {
          const field = fieldById(row.field)!;
          const operator = field.operators.find((o) => o.value === row.operator);
          const value = valueLabel(field, row.value);
          return (
            <ButtonGroup
              key={row.field}
              data-slot="filter-chip"
              aria-label={[field.label, operator?.label, operator && value]
                .filter(Boolean)
                .join(' ')}
            >
              <div className={cn(PART_CLASS, 'cursor-default gap-1.5')} title={field.label}>
                {field.icon}
                <span>{field.label}</span>
              </div>

              <Popover
                open={isOpen(row.field, 'operator')}
                onOpenChange={setPartOpen(row.field, 'operator')}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      PART_CLASS,
                      'cursor-default hover:bg-accent',
                      operator && 'text-muted-foreground',
                    )}
                  >
                    {operator?.label ?? 'Select condition'}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className={cn(POPOVER_CLASS, 'min-w-40')}
                  onCloseAutoFocus={skipFocusOnHandoff}
                >
                  <FilterOptionList
                    label={field.label}
                    options={field.operators}
                    selected={row.operator ? [row.operator] : []}
                    onSelect={(next) => {
                      updateRow(row.field, { operator: next });
                      if (row.value.length) return setOpenPart(null);
                      handoffRef.current = true;
                      setOpenPart({ field: row.field, part: 'value' });
                    }}
                  />
                </PopoverContent>
              </Popover>

              {operator && (
                <Popover
                  open={isOpen(row.field, 'value')}
                  onOpenChange={setPartOpen(row.field, 'value')}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={value}
                      title={
                        row.value.length > 1
                          ? `${value}: ${row.value.map((v) => field.options.find((o) => o.value === v)?.label ?? v).join(', ')}`
                          : undefined
                      }
                      className={cn(PART_CLASS, 'cursor-default hover:bg-accent')}
                    >
                      {value}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className={cn(POPOVER_CLASS, 'min-w-48')}>
                    <FilterOptionList
                      label={field.label}
                      options={field.options}
                      selected={row.value}
                      multiple={field.multiple}
                      searchPlaceholder={field.searchPlaceholder}
                      onSelect={(next) => {
                        if (!field.multiple) {
                          updateRow(row.field, { value: [next] });
                          setOpenPart(null);
                          return;
                        }
                        updateRow(row.field, {
                          value: row.value.includes(next)
                            ? row.value.filter((v) => v !== next)
                            : [...row.value, next],
                        });
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label={`${field.label} filter options`}
                    className="bg-background dark:bg-input/30"
                  >
                    <EllipsisVertical />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => commit(rows.filter((r) => r.field !== row.field))}
                  >
                    <Trash2 />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          );
        })}

        {/* One flex item, so Add filter and Clear wrap together. */}
        <div className="flex items-center gap-1.5">
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size={compact ? 'icon-sm' : 'sm'}
                aria-label="Add filter"
                className={cn(!compact && 'w-8 px-0 md:w-auto md:px-3')}
              >
                <ListFilterPlus />
                {compact ? null : <span className="hidden md:inline">Add filter</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className={cn(POPOVER_CLASS, 'min-w-56')}
              onCloseAutoFocus={skipFocusOnHandoff}
            >
              <FilterOptionList
                label="Attributes"
                searchPlaceholder="Search attributes..."
                options={fields.map((f) => ({
                  value: f.id,
                  label: f.label,
                  icon: f.icon,
                  disabled: inUse.has(f.id),
                }))}
                selected={[]}
                onSelect={(id) => addField(id as FieldId)}
              />
            </PopoverContent>
          </Popover>

          {compact && (
            <Button variant="outline" size="sm" onClick={() => commit([])}>
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
